import _ from "lodash";
/**--external-- */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**--internal-- */
import {
  Button,
  ChecklistDropdown,
  IconButton,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  CircleOutlined,
  DeleteOutlined,
  DownArrowOutlined,
  TagAddOutlined,
  TickCircleFilled,
  UpArrowOutlined,
} from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";

/**--relative-- */
import classes from "./ElementContextMenu.scss";
import { isDraggable, isDropzone } from "../DragAndDrop/DragAndDropUtils";
import { DRAG_AND_DROP_CATEGORY } from "../DragAndDrop/DragAndDropConstant";

const DROPZONE_TAGGED_ANSWER_KEY = "dropzoneTaggedAnswer";

// * Menu separator
const Separator = () => <div className={classes.contextMenuButtonSeparator} />;

/**
 *
 * @param { node, onClick  }
 * @returns Button to remove element from canvas
 */
const RemoveItemButton = ({ node, onClick = () => {} }) => {
  const handleRemoveItem = () => {
    const id = node.id();
    onClick(id);
  };

  return (
    <IconButton
      dsVersion="2.0"
      variant="neutral"
      type="plain"
      _legacy_variant="plain"
      onClick={handleRemoveItem}
      icon={<DeleteOutlined size="xx-small" variant="critical" />}
      testId="worksheet-elementContextMenu-removeItem"
    ></IconButton>
  );
};

/**
 *
 * @param { node, onClick  }
 * @returns Button to order elements on canvas category wise
 */
const OrderItemButtons = ({ node, onOrderElement = () => {} }) => {
  const { t } = useI18n();

  const handleOrderElement = type => {
    const id = node.id();
    onOrderElement(id, type);
  };

  return (
    <>
      <Tooltip tooltip={t("workbook:bring_to_front")}>
        <IconButton
          dsVersion="2.0"
          type="plain"
          variant="neutral"
          _legacy_variant="plain"
          onClick={() => handleOrderElement("UP")}
          icon={<UpArrowOutlined size="xx-small" variant="subtle" />}
          testId="worksheet-elementContextMenu-orderElementUp"
        ></IconButton>
      </Tooltip>
      <Tooltip tooltip={t("workbook:send_to_back")}>
        <IconButton
          dsVersion="2.0"
          type="plain"
          variant="neutral"
          _legacy_variant="plain"
          onClick={() => handleOrderElement("DOWN")}
          icon={<DownArrowOutlined size="xx-small" variant="subtle" />}
          testId="worksheet-elementContextMenu-orderElementDown"
        ></IconButton>
      </Tooltip>
    </>
  );
};

/**
 *
 * @param { node, onClick }
 * @returns Button to convert `draggable <> decorative` converter
 */
const DraggableToggleButton = ({ node, isDraggable, onClick }) => {
  const { t } = useI18n();

  const updateToggleState = () => {
    const id = node.id();
    const category = isDraggable
      ? DRAG_AND_DROP_CATEGORY.DECORATIVE
      : DRAG_AND_DROP_CATEGORY.DRAGGABLE;

    onClick({ id, category });
  };

  return (
    <Button
      onClick={updateToggleState}
      dsVersion="2.0"
      type="plain"
      variant="neutral"
      _legacy_variant="plain"
      icon={
        isDraggable ? (
          <TickCircleFilled size="x-small" variant="success" />
        ) : (
          <CircleOutlined size="x-small" variant="subtle" />
        )
      }
      testId="worksheet-elementContextMenu-draggableOrDecorative"
    >
      {t("worksheet:draggable")}
    </Button>
  );
};

/**
 *
 * @param { children node, onAddItem }
 * @returns
 */
export const TagAnswerButton = ({
  node = {},
  itemId = null,
  elementLayer = null,
  children = null,
  onAddCorrectElement = () => {},
}) => {
  const { t } = useI18n();
  const valueRef = useRef([]);
  const [value, setValue] = useState([]);
  const id = itemId || node?.id();

  useEffect(() => {
    const initialValues = _.isFunction(node?.getAttr)
      ? node.getAttr(DROPZONE_TAGGED_ANSWER_KEY)
      : [];
    valueRef.current = initialValues;
    setValue(initialValues);

    return () => {
      // * Save on changing or un-focusing the node
      if (!_.isEqual(initialValues, valueRef.current)) {
        onAddCorrectElement({
          id: id,
          correctElements: _.clone(valueRef.current),
        });
      }
    };
  }, [id]);

  /**
   * @description
   * Getting all possible options i.e. draggable items as label and value object
   */
  const getOptions = () => {
    const layer =
      elementLayer || (_.isFunction(node?.getLayer) ? node?.getLayer() : null);
    if (!layer) return [];
    const allElements = _.get(layer, "children", []);
    const options = [];
    _.forEach(allElements, element => {
      const elementDetails = _.split(element.name(), "#");
      const category = _.get(elementDetails, "0");
      const type = _.get(elementDetails, "1");
      if (isDraggable(category)) {
        /**
         * Checking type of DRAGGABLE element and getting label
         * if IMAGE -> returning a dom element of image with src
         * if TEXT -> return trimmed text as option label
         * @returns Label of Dropdown list
         */
        const getLabel = () => {
          if (type === "TEXT") return _.trim(element?.text());
          const imageSrc = _.get(element?.image(), "src");
          return (
            <img
              src={imageSrc}
              style={{ height: "80px", borderRadius: "8px" }}
              alt="Draggable Element"
            />
          );
        };

        options.push({
          type,
          value: element.id(),
          label: getLabel(),
        });
      }
    });

    // * returning option sorted by type [TEXT, IMAGE]
    return _.sortBy(options, "type");
  };

  return (
    <ChecklistDropdown
      value={value}
      options={getOptions()}
      placement={children ? "bottomCenter" : "bottomLeft"}
      showSelectedOptionsOnTop={false}
      checklistLabel={t("worksheet:select_correct_answers")}
      checklistContainerClassName={classes.tagAnswerPopupOverlay}
      onChange={opt => {
        valueRef.current = opt;
        setValue(opt);

        // * Updating data without redrawing
        onAddCorrectElement({
          id: id,
          correctElements: _.clone(valueRef.current),
          updateOnlyAnswer: true,
        });
      }}
      testId="worksheet-elementContextMenu-draggableItems"
    >
      {children || (
        <Button
          dsVersion="2.0"
          type="plain"
          variant="neutral"
          _legacy_variant="plain"
          icon={<TagAddOutlined size="xx-small" variant="subtle" />}
        >
          {t("worksheet:tag_answers")}
        </Button>
      )}
    </ChecklistDropdown>
  );
};

/**
 *
 *
 *
 *
 * @param {*} props
 * @returns ElementContextMenu
 */
const ElementContextMenu = props => {
  const node = _.get(props, "node");
  if (!node) return null;

  const category = _.first(_.split(node.name(), "#"));
  const dropzone = isDropzone(category);

  return (
    <>
      {!dropzone ? (
        <>
          <DraggableToggleButton
            node={node}
            onClick={_.get(props, "onChange", () => {})}
            isDraggable={category === DRAG_AND_DROP_CATEGORY.DRAGGABLE}
          />
          <Separator />
          <OrderItemButtons
            node={node}
            onOrderElement={_.get(props, "onOrderElement", () => {})}
          />
        </>
      ) : (
        <TagAnswerButton
          node={node}
          onAddCorrectElement={_.get(props, "onChange", () => {})}
        />
      )}
      <Separator />
      <RemoveItemButton
        node={node}
        onClick={_.get(props, "onRemove", () => {})}
      />
    </>
  );
};

export default ElementContextMenu;

ElementContextMenu.defaultProps = {
  node: {},
  onRemove: () => {},
  onChange: () => {},
  onOrderElement: () => {},
};

ElementContextMenu.propTypes = {
  node: PropTypes.any,
  onRemove: PropTypes.func,
  onChange: PropTypes.func,
  onOrderElement: PropTypes.func,
};
